import { type FC, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { IconStatsClose, IconSwipeUp } from 'assets/svg/sprite'
import { ShowMore } from 'components/ShowMore/ShowMore'
import { getStoryBackground } from 'components/UI/BackgroundPicker/BackgroundPicker'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { PreviewBackgroundGradient, ThreeDotsTrigger } from 'components-ref/atoms'
import { LastPostInfo, PaymentModal, PostGoal } from 'components-ref/molecules'
import { Modal } from 'components-ref/molecules/modal/modal'
import { usePreviewContext } from 'context/previewContext'
import PreviewActions from 'features/Preview/PreviewActions/PreviewActions'
import { useOrientation } from 'helpers/helperHooks'
import { extractMedia } from 'helpers/media'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { useUserProfile } from 'store'
import { type IPost, type ITipPayload } from 'types/interfaces/ITypes'

interface Props {
  post: IPost
  showControls: boolean
  isFirstPost: boolean
  disabled: boolean
  hasNextPage: boolean
  minimizeFn: (_fileIndex: number, _type: string) => void
  toggleControls: () => void
  tipFn: (payload: ITipPayload) => void
  isLastPost?: boolean
  setIsOptionsNavOpen?: (val: boolean) => void
}

const SinglePostPreviewGoal: FC<Props> = ({
  post,
  showControls,
  disabled,
  toggleControls,
  tipFn,
  hasNextPage,
  isLastPost = false,
  setIsOptionsNavOpen
}) => {
  const userData = useUserProfile()!
  const navigate = useNavigate()
  const deviceOrientation = useOrientation()
  const { t } = useTranslation()

  const {
    query,
    setCommentsActive,
    minimized,
    clearModal
  } = usePreviewContext()

  const [selectedTipValue, setSelectedTipValue] = useState(0)
  const [confirmBuyModalOpen, setConfirmBuyModalOpen] = useState(false)

  const [paymentMethod, setPaymentMethod] = useState<{
    method: 'card' | 'deposit'
    cardId: string | undefined
  }>({
    method: 'deposit',
    cardId: undefined
  })

  const postFile = useMemo(
    () => ({
      userId: post.user_id,
      name: post.user?.display_name,
      text: post.body,
      mentions: post.tags,
      media: extractMedia(post),
      minimizedName: post.user?.display_name,
      minimizedAvatar:
        post.user?.cropped_avatar?.url ??
        post.user?.avatar?.url ??
        placeholderAvatar
    }),
    [
      post,
      post.user_id,
      post.user?.display_name,
      post.user?.cropped_avatar?.url,
      post.user?.avatar?.url,
      post.body,
      post.tags
    ]
  )

  if (!post.goal) return null

  const goal = post.goal
  const isPortrait = deviceOrientation?.type?.includes('portrait') ?? true

  const thumbnailUrl =
      goal.background_src_thumb ??
      goal.background_src

  const fullImageUrl = goal.background_src

  const cachedThumbnail = thumbnailUrl
      ? getCachedBlob(thumbnailUrl)
      : null

  const cachedFullImage = fullImageUrl
      ? getCachedBlob(fullImageUrl)
      : null

  const backgroundUrl =
      cachedThumbnail ??
      thumbnailUrl

  const foregroundUrl =
      cachedFullImage ??
      fullImageUrl ??
      backgroundUrl

  const hasBackgroundImage = Boolean(
      thumbnailUrl || fullImageUrl
  )

  const goalBackgroundImage = hasBackgroundImage
      ? backgroundUrl
          ? `url("${backgroundUrl}")`
          : undefined
      : getStoryBackground(goal.background_color)

  const goalBackgroundClass = hasBackgroundImage
      ? 'preview__background preview__background--goal-image'
      : 'preview__background preview__background--goal-gradient'

  const noDefaultCard =
    userData?.default_card == null || String(userData?.default_card) === ''

  const noWalletDeposit =
    userData?.wallet_deposit == null || Number(userData?.wallet_deposit) <= 0

  const showAddCard = noDefaultCard && noWalletDeposit

  const onTipPost = (val: number) => {
    if (disabled) return

    const payload: ITipPayload = {
      amount: val.toString(),
      post_id: post.id,
      payment_method: paymentMethod.method,
      card_id: paymentMethod.cardId
    }

    tipFn(payload)
    setConfirmBuyModalOpen(false)
  }

  const handleTip = () => {
    onTipPost(selectedTipValue)
  }

  return (
    <>
      <div className="preview__close" onClick={() => clearModal()}>
        {minimized ? <IconStatsClose color="#A3A3A3" /> : <IconStatsClose />}
      </div>

      {showControls && (
        <ThreeDotsTrigger
          onClick={e => {
            e.stopPropagation()
            setIsOptionsNavOpen?.(true)
          }}
        />
      )}

      <div className="preview__file--wrapper">
        {showControls && (
          <>
            <ShowMore
              userId={postFile.userId}
              name={postFile.name}
              time={post.created_at}
              text={postFile.text ?? ''}
              mentions={postFile.mentions}
            />

            <PreviewActions
              stats={{
                overallRevenue: post.overall_revenue,
                purchasesCount: post.purchases_count,
                purchasesSum: post.purchases_sum,
                tipsCount: post.tips_count,
                tipsSum: post.tips_sum,
                conversionRate: post.conversion_rate,
                uniqueImpressions: post.unique_impressions,
                views: post.views,
                uniqueViews: post.unique_views,
                price: post.price
              }}
              modelData={{
                postId: post.id,
                modelId: post.user.id,
                avatarSrc:
                  post.user.avatar?.url ??
                  post.user.cropped_avatar?.url ??
                  placeholderAvatar
              }}
              query={query}
              postId={post.id}
              isLiked={post.isLiked}
              likes={post.likes_count}
              comments={post.comment_count}
              setCommentsActive={setCommentsActive}
            />
          </>
        )}

        <div
          className={`preview__file preview__file--goal ${
            isPortrait ? 'portrait' : 'landscape'
          }`}
          onClick={e => {
            const el = e.target as HTMLElement

            if (
              el.classList.contains('goalBox__options') ||
              el.classList.contains('goalBox__option')
            ) {
              return
            }

            toggleControls()
          }}
        >
          <div
            className={goalBackgroundClass}
            style={{
              backgroundImage: goalBackgroundImage
            }}
          />

          {hasBackgroundImage && !minimized && <PreviewBackgroundGradient />}

          {foregroundUrl && (
              <img
                  className="preview__goal-image"
                  src={foregroundUrl}
                  alt="goal"
              />
          )}

          <div className="preview__goal-bottom">
            {isLastPost && !hasNextPage && <LastPostInfo isGoal />}

            <div className="preview__poll">
              <PostGoal
                goalData={goal}
                setTip={val => {
                  setSelectedTipValue(val)
                  setConfirmBuyModalOpen(true)
                }}
                isMyPost={post.user_id === userData.id}
                disabled={disabled}
              />
            </div>
          </div>

          {confirmBuyModalOpen && (
            <Modal
              onClose={() => setConfirmBuyModalOpen(false)}
              title={showAddCard ? 'Add A Card' : 'Select payment method'}
              closeOnLayerClick
              isLayerTransparent={false}
            >
              {showAddCard ? (
                <ConfirmModal
                  customClass="confirmModal--add-card"
                  confirmText="Add Card"
                  onCancel={() => setConfirmBuyModalOpen(false)}
                  body={
                    <div>
                      <p className="add-card-title-spacing">
                        You don&apos;t have a saved card or wallet funds. Please
                        add a card to continue.
                      </p>
                    </div>
                  }
                  confirmFn={() => {
                    navigate('/settings/general/wallet')
                    setConfirmBuyModalOpen(false)
                  }}
                />
              ) : (
                <PaymentModal
                  purchaseCb={handleTip}
                  paymentMethod={paymentMethod}
                  setPaymentMethod={setPaymentMethod}
                  price={selectedTipValue}
                />
              )}
            </Modal>
          )}

{/*           {shouldShowFirstTourGuide() && (
            <div className="preview__swipe--icon">
              <IconSwipeUp opacity={isPortrait ? 1 : 1} />
              <p>{t('swipeUpForNextPost')}</p>
            </div>
          )} */}
        </div>
      </div>
    </>
  )
}

export default SinglePostPreviewGoal